/**
 * @class NumDrawer
 * @extends HTMLElement
 * @description A custom web component for a sliding drawer with configurable size, direction, background color, y box-shadow.
 * @attribute {boolean} open - Indicates whether the drawer is open.
 * @attribute {string} size - The size of the drawer (e.g., '300px' or '{"default": "300px", "sm": "200px", "md": "400px"}').
 * @attribute {string} direction - The direction from which the drawer opens ('left', 'right', 'top', 'bottom').
 * @attribute {string} bg-color - The background color of the drawer panel.
 * @attribute {string} z-index - The z-index of drawer .
 * @attribute {string} body-scroll - The body scroll behavior when the drawer is open ('hidden', 'scroll', 'auto').
 */
class NumDrawer extends HTMLElement {
  static get observedAttributes() {
    return ['open', 'size', 'direction', 'bg-color', 'z-index', 'body-scroll'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._isOpen = false;
    this._panel = null;
    this._overlay = null;
    this._trigger = null;
  }

  connectedCallback() {
    this._render();
    this._panel = this.shadowRoot.querySelector('.num-drawer__panel');
    this._overlay = this.shadowRoot.querySelector('.num-drawer__overlay');
    this._attachEventListeners();
    this._updateDrawerState();
    window.addEventListener('resize', this._updateDrawerState.bind(this));
    document.addEventListener('keydown', this._handleKeyDown.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._updateDrawerState.bind(this));
    document.removeEventListener('keydown', this._handleKeyDown.bind(this));
  }

  attributeChangedCallback(name) {
    if (name === 'open') {
      this._isOpen = this.hasAttribute('open');
    }
    this._updateDrawerState();
  }

  open(trigger = null, elementToFocus = null) {
    if (!this._isOpen) {
      this._trigger = trigger;
      this.setAttribute('open', '');
      this._dispatchEvent('onOpen');
      const slot = this._panel.querySelector('slot');
      const assignedElements = slot.assignedElements();
      const focusElement = elementToFocus || findFirstElement(assignedElements, 'input:not([type="hidden"])');
      this._panel.addEventListener(
        'transitionend',
        () => {
          if (focusElement) {
            trapFocus(this._panel, focusElement);
          }
        },
        { once: true }
      );
    }
  }

  close() {
    if (this._isOpen) {
      this.removeAttribute('open');
      this._dispatchEvent('onClose');
      removeTrapFocus(this._trigger);
    }
  }

  toggle(trigger = null) {
    this._isOpen ? this.close() : this.open(trigger);
  }

  _dispatchEvent(eventName) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, composed: true }));
  }

  _attachEventListeners() {
    this._overlay?.addEventListener('click', () => this.close());
  }

  _handleKeyDown(event) {
    if (event.key === 'Escape' && this._isOpen) {
      this.close();
    }
  }

  _getSize() {
    const sizeAttr = this.getAttribute('size');
    let size = '300px';

    if (sizeAttr) {
      try {
        const sizeObj = JSON.parse(sizeAttr);
        const width = window.innerWidth;

        if (width >= 1200 && sizeObj.lg) {
          size = sizeObj.lg;
        } else if (width >= 992 && sizeObj.md) {
          size = sizeObj.md;
        } else if (width >= 768 && sizeObj.sm) {
          size = sizeObj.sm;
        } else {
          size = sizeObj.default || size;
        }
      } catch (e) {
        size = sizeAttr;
      }
    }

    return size;
  }

  _updateDrawerState() {
    const size = this._getSize();
    const direction = this.getAttribute('direction') || 'left';
    const bgColor = this.getAttribute('bg-color') || '#fff';
    const zIndex = this.getAttribute('z-index') || '1000';
    const bodyScroll = this.getAttribute('body-scroll') || 'auto';

    const styles = {
      left: {
        top: 0,
        left: 0,
        width: size,
        height: '100dvh',
        transform: this._isOpen ? 'translateX(0)' : 'translateX(-100%)',
      },
      right: {
        top: 0,
        right: 0,
        width: size,
        height: '100dvh',
        transform: this._isOpen ? 'translateX(0)' : 'translateX(100%)',
      },
      top: {
        top: 0,
        left: 0,
        width: '100dvw',
        height: size,
        transform: this._isOpen ? 'translateY(0)' : 'translateY(-100%)',
      },
      bottom: {
        bottom: 0,
        left: 0,
        width: '100dvw',
        height: size,
        transform: this._isOpen ? 'translateY(0)' : 'translateY(100%)',
      },
    };

    if (this._panel && this._overlay) {
      this.style.zIndex = zIndex;
      Object.assign(this._panel.style, styles[direction]);
      this._panel.style.backgroundColor = bgColor;
      this._overlay.style.visibility = this._isOpen ? 'visible' : 'hidden';
      this._overlay.style.opacity = this._isOpen ? '1' : '0';
    }

    document.body.style.overflow = this._isOpen ? bodyScroll : '';
  }

  _render() {
    this.shadowRoot.innerHTML = `
        <style>${this._getStyles()}</style>
        <div class="num-drawer__overlay"></div>
        <div class="num-drawer__panel" role="dialog" aria-modal="true" tabindex="-1">
          <slot></slot>
        </div>
      `;
    this.style.display = 'block';
  }

  _getStyles() {
    return `
        :host {
          display: block;
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 1000;
          pointer-events: none;
        }
  
        .num-drawer__overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.5);
          visibility: hidden;
          opacity: 0;
          transition: visibility 0s, opacity 0.3s ease;
          z-index: 910;
          pointer-events: auto;
          backdrop-filter: blur(6.15px);
          -webkit-backdrop-filter: blur(6.15px);
        }
  
        .num-drawer__panel {
          position: absolute;
          background-color: #fff;
          z-index: 920;
          pointer-events: auto;
        }
  
        :host([open]) .num-drawer__overlay {
          visibility: visible;
          opacity: 1;
        }
  
        :host([open]) .num-drawer__panel {
          transform: translateX(0);
          transition: transform 0.3s ease;
        }
      `;
  }
}

customElements.define('num-drawer', NumDrawer);
